import { useEffect } from "react"
import { EditInput } from "@/components"
import { PermissionGuard } from "@/components/Auth"
import {
  useUpdateOrganizationApi,
  UpdateOrganizationRequest,
  organizationResponseToOrganization,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { Organization, Permission } from "@/types"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { useTranslation } from "react-i18next"

interface OrganizationNameInputProps {
  organization: Organization
}

const OrganizationNameInput = ({
  organization,
}: OrganizationNameInputProps) => {
  const { alertToast } = useToast()
  const { t } = useTranslation()
  const [setCurrentOrganization] = useStore((state) => [
    state.setCurrentOrganization,
  ])

  // API
  const {
    error: updateOrganizationError,
    data: updateOrganizationResp,
    fetchFn: updateOrganization,
  } = useUpdateOrganizationApi(organization.id)

  useEffect(() => {
    if (!updateOrganizationResp || !updateOrganizationResp.data) return
    setCurrentOrganization(
      organizationResponseToOrganization(updateOrganizationResp.data)
    )
  }, [updateOrganizationResp, setCurrentOrganization])

  useEffect(() => {
    if (!updateOrganizationError) return
    console.error("Failed to update organization name", updateOrganizationError)

    alertToast({
      title: "Failed to update user name",
      description: getUserFacingApiErrorMessage(
        updateOrganizationError,
        t,
        updateOrganizationResp?.message || "Failed to update user name"
      ),
    })
  }, [updateOrganizationError, updateOrganizationResp, alertToast, t])

  const handleSubmit = (value: string) => {
    if (value === organization.name) return

    const payload: UpdateOrganizationRequest = { name: value }
    updateOrganization({ jsonBody: payload })
  }

  return (
    <PermissionGuard permission={Permission.OrgChangeName} disabled>
      <EditInput
        className="flex-wrap space-y-1 justify-end"
        value={organization.name}
        onSave={handleSubmit}
      />
    </PermissionGuard>
  )
}

export default OrganizationNameInput
